import React, { useEffect, useRef } from 'react';
import ImageCarousel from '../components/ImageCarousel';
import { tinyHouseImages } from '../mock';
import SEO from '../components/SEO';
import PageActions from '../components/PageActions';

const TinyHousePage = () => {
  useEffect(() => {
    if (window.scrollY !== 0) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, []);

  const sections = [
    {
      id: 'living-room',
      title: 'Your Living Room',
      images: tinyHouseImages.livingRoom,
      description: 'The living room is the cabin\'s cosy heart, warmed by a crackling wood stove that invites you to slow down. Large windows frame the surrounding views, bringing the picturesque landscape gently inside.'
    },
    {
      id: 'kitchen',
      title: 'Your Kitchen',
      images: tinyHouseImages.kitchen,
      description: 'Thoughtfully equipped to make nourishing meals. The open layout, with the necessary kitchen appliances, keeps you connected to companions and the view beyond the window.'
    },
    {
      id: 'bedrooms',
      title: 'Your Bedrooms',
      images: tinyHouseImages.bedroom,
      description: 'The master bedroom has a king-size bed and calming views; wake to birdsong and soft morning light. A place for deep rest, unhurried moments, and easy daytime relaxation. A second room can be arranged as a bedroom, hobby room or quiet retreat. The cabin comfortably sleeps up to five people.'
    },
    {
      id: 'bathroom',
      title: 'Your Bathroom',
      images: tinyHouseImages.bathroom,
      description: 'A simple, warm sanctuary. The star-view shower lets you bathe beneath the sky, while heated towel rails and good ventilation ensure comfort. Natural materials and clean lines give the space a modern, timeless feel.'
    },
    {
      id: 'terrace',
      title: 'Your Covered Terrace',
      images: tinyHouseImages.terrace,
      description: 'A covered outdoor space invites you to linger over morning coffee, meals under the stars, or simply watching the day unfold. There is space for a delightful hot tub to relax and let the line between inside and outside dissolve.'
    }
  ];

  return (
    <div className="bg-[#f5f1e8]">
      <SEO 
        title="Your Country Cabin - Luxury Country Cabin in Dorset | Your Setl"
        description="Discover our unique country cabins in Dorset. Two bedrooms, wood stove, star-view shower, and covered terrace. Sleeps up to 5 guests. Where comfort meets nature on a working farm."
        keywords="country cabin Dorset, luxury cabin Dorset, eco cabin accommodation, farm stay UK, Dorset country cabin, glamping Dorset, countryside retreat, green roof cabin"
        ogImage="https://res.cloudinary.com/dvypx0zh5/image/upload/f_auto,q_auto:best/v1771402938/DSC_7933_hl4uqd.jpg"
        url="https://www.yoursetl.co.uk/your-country-cabin"
      />
      <PageActions />
      {/* Hero Section */}
      <section className="relative h-screen flex items-end overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${tinyHouseImages.hero})`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent" />
        </div>

        <div className="relative z-10 px-8 md:px-16 pb-16 md:pb-20 max-w-3xl">
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-light text-[#f5f1e8] leading-tight"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Your Country Cabin
          </h1>
          <div className="w-16 h-px bg-[#f5f1e8]/70 my-5" />
          <p
            className="text-base md:text-lg font-light text-[#f5f1e8] tracking-wide"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Where design meets intention. Every detail crafted to help you slow down, breathe deeply, and reconnect with what's essential.
          </p>
        </div>
      </section>

      {/* Scrolling Sections */}
      {sections.map((section, index) => (
        <section
          key={section.id}
          id={section.id}
          className={`min-h-screen flex items-center py-20 px-6 ${
            index % 2 === 0 ? 'bg-[#f5f1e8]' : 'bg-[#e8e2d8]'
          }`}
        >
          <div className="max-w-7xl mx-auto w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Text Content */}
              <div className={index % 2 === 0 ? 'order-1' : 'order-1 lg:order-2'}>
                <h2
                  className="text-4xl md:text-5xl font-light tracking-wide text-[#3a2f28] mb-6"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {section.title}
                </h2>
                <p
                  className="text-xl leading-relaxed text-[#3a2f28]/85"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {section.description}
                </p>
              </div>

              {/* Image Carousel */}
              <div className={index % 2 === 0 ? 'order-2' : 'order-2 lg:order-1'}>
                <div className="relative h-[400px] md:h-[550px] rounded-lg overflow-hidden shadow-2xl">
                  <ImageCarousel images={section.images} alt={section.title} />
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Key Features Summary */}
      <section className="py-20 px-6 bg-[#3a2f28]">
        <div className="max-w-5xl mx-auto">
          <h2
            className="text-3xl md:text-5xl font-light tracking-wide text-[#f5f1e8] mb-12 text-center"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            At a Glance
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { label: 'Capacity', value: 'Sleeps Up To 5 Persons' },
              { label: 'Bedrooms', value: 'Two' },
              { label: 'Views', value: 'Wide Nature Panoramas' },
              { label: 'Design', value: 'Inside-Outside Connection With Covered Terrace' },
              { label: 'Heating', value: 'Wood Stove And Gas Heating' },
              { label: 'Kitchen', value: 'Well-Equipped (Fridge, Gas Hob, Electric Oven)' },
              { label: 'Bathroom', value: 'Fully Equipped - Star-View Shower' },
              { label: 'Covered terrace', value: '13.4m² - Space For Wood Burning Hot Tub' },
              { label: 'Atmosphere', value: 'Warmth, Silence, Simplicity' },
              { label: 'Eco', value: 'Green Roof' }
            ].map((feature) => (
              <div key={feature.label} className="text-center">
                <p
                  className="text-sm uppercase tracking-wider text-[#d4a574] mb-2"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {feature.label}
                </p>
                <p
                  className="text-lg text-[#f5f1e8]"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {feature.value}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Space Beyond the Cabin */}
      <section className="py-16 lg:py-20 px-6 bg-[#f5f1e8]">
        <div className="max-w-5xl mx-auto">
          <h2
            className="text-3xl md:text-4xl font-light tracking-wide text-[#2a3a2a] mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Space beyond the cabin
          </h2>
          <p
            className="text-base lg:text-lg leading-relaxed text-[#3a2f28]/85 mb-4 max-w-3xl"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Life at Your Setl extends far beyond the cabin itself. Each plot includes approximately 260 sq m of outdoor space, with room to gather, cook, grow and slow down.
          </p>
          <p
            className="text-base leading-relaxed text-[#3a2f28]/70 mb-6"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            The intention is simple: space to live properly, both inside and out.
          </p>
          <ul className="flex flex-wrap gap-x-8 gap-y-2 mb-10" style={{ fontFamily: 'Inter, sans-serif' }}>
            {[
              '57 sqm internal living space',
              'Approximately 260 sqm outdoor area',
              'Two parking spaces',
              'Private outdoor storage',
            ].map((item) => (
              <li key={item} className="flex items-center gap-2 text-[#3a2f28]/80">
                <span className="text-[#d4a574]">—</span>
                <span className="text-sm">{item}</span>
              </li>
            ))}
          </ul>
          <div className="rounded-lg overflow-hidden shadow-2xl">
            <img
              src="/siteplan.png"
              alt="Your Setl site plan - Dorset countryside layout"
              className="w-full h-auto block"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default TinyHousePage;